import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Zap, Crown, Rocket, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UpgradePage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, usageRes] = await Promise.all([
        axios.get(`${API}/plans`),
        axios.get(`${API}/usage/summary`)
      ]);
      setPlans(plansRes.data);
      setCurrentPlan(usageRes.data.plan.id);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    if (planId === currentPlan) {
      toast.info('Você já está neste plano!');
      return;
    }

    setProcessingPlan(planId);
    
    // Aqui você integraria com o sistema de pagamento (PIX, Stripe, etc.)
    // Por enquanto, vamos simular uma atualização
    try {
      // Simular processamento
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success(`Upgrade para ${planId.toUpperCase()} iniciado! Você receberá instruções de pagamento por email.`);
      
      // Redirecionar para página de pagamento
      // navigate(`/payment?plan=${planId}`);
      
    } catch (error) {
      toast.error('Erro ao processar upgrade. Tente novamente.');
    } finally {
      setProcessingPlan(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'starter': return <Zap className="h-8 w-8" />;
      case 'pro': return <Rocket className="h-8 w-8" />;
      case 'enterprise': return <Crown className="h-8 w-8" />;
      default: return <Zap className="h-8 w-8" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'starter': return 'from-green-500 to-emerald-600';
      case 'pro': return 'from-blue-500 to-indigo-600';
      case 'enterprise': return 'from-purple-500 to-pink-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Botão Voltar */}
        <button 
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Voltar ao Dashboard
        </button>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            🚀 Escolha seu Plano
          </h1>
          <p className="text-xl text-gray-300">
            Desbloqueie mais recursos e acelere seus ganhos como afiliado!
          </p>
          {currentPlan && (
            <p className="mt-4 text-yellow-400">
              Seu plano atual: <strong>{currentPlan.toUpperCase()}</strong>
            </p>
          )}
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div 
              key={plan.id}
              className={`relative bg-white/10 backdrop-blur-lg rounded-2xl p-8 border-2 transition-all duration-300 hover:scale-105 ${
                plan.popular 
                  ? 'border-yellow-400 shadow-lg shadow-yellow-400/20' 
                  : plan.id === currentPlan
                    ? 'border-green-400'
                    : 'border-white/20'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-4 py-1 rounded-full text-sm font-bold">
                    ⭐ MAIS POPULAR
                  </span>
                </div>
              )}

              {/* Current Plan Badge */}
              {plan.id === currentPlan && (
                <div className="absolute -top-4 right-4">
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    ✓ SEU PLANO
                  </span>
                </div>
              )}

              {/* Plan Icon */}
              <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${getPlanColor(plan.id)} flex items-center justify-center text-white mb-6 mx-auto`}>
                {getPlanIcon(plan.id)}
              </div>

              {/* Plan Name */}
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                {plan.name}
              </h2>

              {/* Price */}
              <div className="text-center mb-6">
                <span className="text-5xl font-bold text-white">R$ {plan.price}</span>
                <span className="text-gray-400">/mês</span>
              </div>

              {/* Limits */}
              <div className="bg-white/5 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">🔗 Links</span>
                  <span className="text-white font-bold">
                    {plan.link_limit === -1 ? 'Ilimitado' : plan.link_limit}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">🤖 Conteúdo IA</span>
                  <span className="text-white font-bold">
                    {plan.ai_content_limit === -1 ? 'Ilimitado' : plan.ai_content_limit}
                  </span>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start text-gray-300">
                    <Check className="h-5 w-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                onClick={() => handleUpgrade(plan.id)}
                disabled={plan.id === currentPlan || processingPlan === plan.id}
                className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
                  plan.id === currentPlan
                    ? 'bg-green-500/20 text-green-400 cursor-not-allowed border border-green-500'
                    : processingPlan === plan.id
                      ? 'bg-gray-500 text-gray-300 cursor-wait'
                      : plan.popular
                        ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white hover:from-yellow-600 hover:to-orange-700'
                        : `bg-gradient-to-r ${getPlanColor(plan.id)} text-white hover:opacity-90`
                }`}
              >
                {plan.id === currentPlan 
                  ? '✓ Plano Atual'
                  : processingPlan === plan.id
                    ? 'Processando...'
                    : 'Escolher Plano'
                }
              </button>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-16 bg-white/5 backdrop-blur-lg rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">❓ Perguntas Frequentes</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Como funciona o upgrade?</h3>
              <p className="text-gray-400">Ao fazer upgrade, seu plano é atualizado imediatamente e você ganha acesso a todos os novos recursos.</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Posso cancelar a qualquer momento?</h3>
              <p className="text-gray-400">Sim! Você pode cancelar ou fazer downgrade a qualquer momento sem taxas adicionais.</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Os limites são mensais?</h3>
              <p className="text-gray-400">Os limites de links são totais (acumulativos). Os limites de conteúdo IA são renovados mensalmente.</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Aceita PIX?</h3>
              <p className="text-gray-400">Sim! Aceitamos PIX, cartão de crédito e PayPal para máxima conveniência.</p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ← Voltar ao Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;
